// Gyms. Marko trains in two, and the same movement is not the same exercise
// across them: a cable tower, a pin stack and a dumbbell rack all load differently
// even when the machine "is" the same. So weights only compare within one gym.
//
// What is gym-scoped: last-session targets, the progression engine, stall
// detection (see repo.exerciseHistory), and the +/- step size.
// What is NOT: weekly volume and frequency counts, body measurements, and the
// exercise catalog itself — a hard set counts wherever it happened.
//
// This deliberately does NOT duplicate the catalog per gym. Two gyms x ~30
// exercises would mean per-gym routines and messier substitutions; scoping the
// history query is the same result for a fraction of the surface area.

import type { Exercise, Gym, GymId, SuggestedAdjustment } from './types';

export const GYMS: Gym[] = [
  { id: 'fitx', name: 'FitX Leinbachplatz', shortName: 'FitX' },
  { id: 'fitness-first', name: 'Fitness First (office)', shortName: 'Fitness First' },
];

// The gym assumed for history logged before gym tracking existed (his home gym,
// and where all but a handful of recent sessions happened). Also the default
// pre-selection when starting a session.
export const DEFAULT_GYM_ID: GymId = 'fitx';

export function getGym(id: GymId | null): Gym | null {
  return GYMS.find((g) => g.id === id) ?? null;
}

export function gymLabel(id: GymId | null): string {
  return getGym(id)?.shortName ?? 'Unknown gym';
}

export function isGymId(v: unknown): v is GymId {
  return typeof v === 'string' && GYMS.some((g) => g.id === v);
}

// Resolve an exercise against a gym: currently the weight increment, which is the
// one property that genuinely differs (FitX dumbbells step 2.5 kg/hand, Fitness
// First 2 kg/hand, so a shared step makes half the rack unreachable by tapping).
//
// Doing this once at the boundary is what keeps the rest of the change small:
// the progression engine and SetRow keep reading `ex.increment` and never learn
// that gyms exist. Returns the same object when nothing differs, so React props
// stay referentially stable.
export function resolveExerciseForGym(ex: Exercise, gymId: GymId | null): Exercise {
  if (gymId == null) return ex;
  const override = ex.incrementByGym?.[gymId];
  if (override == null || override === ex.increment) return ex;
  return { ...ex, increment: override };
}

// Last gym used, remembered in meta so the picker pre-selects it. He trains in
// blocks at one gym, so "same as last time" is right far more often than not.
export const LAST_GYM_META_KEY = 'lastGymId';

// Adapt a coach adjustment to the gym it's about to be applied at.
//
// The two halves of an adjustment travel differently. An absolute weight ("hold at
// 50 kg") describes a load on one gym's machine and means nothing on the other's —
// applied blind it would pin a Fitness First number onto a FitX session, which is
// the exact silent-wrong-number failure gym scoping exists to prevent. Everything
// else — hold, deload, swap, grip, rep range — is a judgement about his elbow and
// applies wherever he trains.
//
// So: keep the intent, drop the number. A `hold` stripped of its weight still
// holds; the planner then pins to THIS gym's last load, which is what "don't add
// load" actually means here. Same for `deload` (-10% of this gym's last).
export function adjustmentForGym(
  adj: SuggestedAdjustment | null,
  gymId: GymId | null,
): SuggestedAdjustment | null {
  if (!adj) return null;
  const from = adj.sourceGymId ?? null;
  // Unknown origin (advice predating gym tracking) is treated as same-gym: it was
  // almost certainly written about the home gym, and dropping weights from all
  // historical advice would be a worse default than honouring it.
  if (from == null || gymId == null || from === gymId) return adj;
  if (adj.suggestedWeightKg == null) return adj;
  return { ...adj, suggestedWeightKg: null };
}

// Whether the above will actually drop something, for UI that wants to say so.
export function adjustmentWeightCrossesGyms(
  adj: SuggestedAdjustment,
  gymId: GymId | null,
): boolean {
  return adj.suggestedWeightKg != null
    && adj.sourceGymId != null
    && gymId != null
    && adj.sourceGymId !== gymId;
}

// Map form of resolveExerciseForGym, for screens that hold the whole catalog.
export function resolveExerciseMapForGym(
  exById: Map<string, Exercise>,
  gymId: GymId | null,
): Map<string, Exercise> {
  if (gymId == null) return exById;
  const out = new Map<string, Exercise>();
  for (const [id, ex] of exById) out.set(id, resolveExerciseForGym(ex, gymId));
  return out;
}
